import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import moment from "moment";
import {
  ArrowRight,
  CalendarDays,
  Clock,
  Download,
  Headset,
  Pause,
  Phone,
  Play,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { formatUSPhoneNumber } from "@/utils/helpers";
import type { ConflictRow } from "@/conflicts/types";
import { isCareTeamUtterance } from "@/conflicts/utils";

const PLAYBACK_SPEEDS = [1, 1.5, 2];

export type CallDetailsParty = { name?: string | null; number?: string | null };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  call: ConflictRow | null;
  icon: ReactNode;
  iconBgClass: string;
  outbound: boolean;
  pill: { label: string; tone: string } | null;
  parties: CallDetailsParty[];
  agentName?: string | null;
  patientName?: string | null;
};

function formatClock(seconds?: number) {
  if (seconds == null || !Number.isFinite(seconds)) return "0:00";
  const total = Math.max(0, Math.floor(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

function formatDurationWords(seconds?: number) {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return "";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m <= 0) return `${s} seconds`;
  return `${m} min ${s} seconds`;
}

/** Calls tab "Full Details" popup: call summary, recording player and transcript. */
export function CallDetailsDialog({
  open,
  onOpenChange,
  call,
  icon,
  iconBgClass,
  outbound,
  pill,
  parties,
  agentName,
  patientName,
}: Props) {
  const recordingUrl = call?.recordingUrl || call?.attachmentUrl;
  const recordingName =
    call?.recordingName || call?.attachmentName || "recording.mp3";
  const utterances = (call?.transcriptionUtterances ?? []).filter((u) =>
    String(u.text ?? "").trim(),
  );
  const plainTranscript = String(call?.transcript ?? "").trim();

  const rawDate = call?.callAt || call?.createdAt;
  const dateLabel = rawDate ? moment(rawDate).format("dddd, MMM D, YYYY") : "";
  const timeLabel = rawDate
    ? new Date(rawDate).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      })
    : "";
  const durationLabel = formatDurationWords(Number(call?.durationSeconds));

  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [speedIndex, setSpeedIndex] = useState(0);

  const fallbackDuration = Number(call?.durationSeconds);
  const totalDuration =
    audioDuration > 0
      ? audioDuration
      : Number.isFinite(fallbackDuration) && fallbackDuration > 0
        ? fallbackDuration
        : 0;
  const progressPercent =
    totalDuration > 0 ? Math.min(100, (currentTime / totalDuration) * 100) : 0;

  // Stop and reset the player whenever the popup opens/closes or the call changes.
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      audio.playbackRate = PLAYBACK_SPEEDS[0];
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setAudioDuration(0);
    setSpeedIndex(0);
  }, [open, recordingUrl]);

  const onAudioTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setCurrentTime(audio.currentTime || 0);
    if (Number.isFinite(audio.duration) && audio.duration > 0) {
      setAudioDuration(audio.duration);
    }
  };

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio || !recordingUrl) return;
    try {
      if (audio.paused) {
        await audio.play();
        setIsPlaying(true);
      } else {
        audio.pause();
        setIsPlaying(false);
      }
    } catch {
      setIsPlaying(false);
    }
  };

  const seekRecording = (value: number) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(value)) return;
    audio.currentTime = value;
    setCurrentTime(value);
  };

  const cycleSpeed = () => {
    const next = (speedIndex + 1) % PLAYBACK_SPEEDS.length;
    setSpeedIndex(next);
    if (audioRef.current) audioRef.current.playbackRate = PLAYBACK_SPEEDS[next];
  };

  const downloadRecording = async () => {
    if (!recordingUrl) return;
    setIsDownloading(true);
    try {
      const response = await fetch(recordingUrl);
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blobUrl = window.URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = recordingName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download recording:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        draggable={false}
        aria-describedby={undefined}
        className="w-[96dvw] overflow-hidden rounded-2xl sm:max-w-2xl"
      >
        {/* Header */}
        <div className="flex items-start gap-3 pr-8">
          <div
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
              iconBgClass,
            )}
          >
            {icon}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <DialogTitle className="text-lg font-semibold text-foreground">
                Full Details
              </DialogTitle>
              <Badge
                variant="outline"
                className={cn(
                  "rounded-full px-2 py-0 text-[11px] font-medium leading-5",
                  outbound
                    ? "border-[#BFDBFE] bg-[#EFF6FF] text-[#2563EB]"
                    : "border-[#A7F3D0] bg-[#ECFDF5] text-[#047857]",
                )}
              >
                • {outbound ? "Outbound Call" : "Inbound Call"}
              </Badge>
              {pill ? (
                <Badge
                  variant="outline"
                  className={cn(
                    "rounded-full px-2 py-0 text-[11px] font-medium leading-5",
                    pill.tone,
                  )}
                >
                  {pill.label}
                </Badge>
              ) : null}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-muted-foreground">
              {dateLabel ? (
                <span className="inline-flex items-center gap-1">
                  <CalendarDays className="h-3.5 w-3.5" />
                  {dateLabel}
                </span>
              ) : null}
              {timeLabel ? (
                <span className="inline-flex items-center gap-1">
                  <span className="hidden sm:inline">•</span>
                  <Clock className="h-3.5 w-3.5" />
                  {timeLabel}
                </span>
              ) : null}
              {durationLabel ? (
                <span>
                  <span className="hidden sm:inline">• </span>Duration:{" "}
                  <span className="font-semibold text-foreground">
                    {durationLabel}
                  </span>
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Call path */}
        <div className="mt-4 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2 text-[12px]">
          {parties.map((party, idx) => {
            const number = party.number ? formatUSPhoneNumber(party.number) : "";
            return (
              <span
                key={`${idx}-${party.name ?? ""}-${party.number ?? ""}`}
                className="inline-flex min-w-0 flex-wrap items-center gap-x-1.5"
              >
                {idx > 0 ? (
                  <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                ) : null}
                <span className="wrap-break-word font-semibold text-foreground">
                  {party.name || number || "—"}
                </span>
                {party.name && number ? (
                  <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                    {number}
                  </span>
                ) : null}
              </span>
            );
          })}
        </div>

        {/* Recording player */}
        <div className="mt-4 border-b border-[#E5E7EB] pb-4">
          {recordingUrl ? (
            // Phones: seek bar drops to its own full-width row; sm and up: one row like the design.
            <div className="flex flex-wrap items-center gap-x-3 gap-y-3 sm:flex-nowrap">
              <audio
                ref={audioRef}
                src={recordingUrl}
                preload="metadata"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                onTimeUpdate={onAudioTimeUpdate}
                onLoadedMetadata={onAudioTimeUpdate}
              />
              <button
                type="button"
                onClick={() => void togglePlayback()}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#2563EB] text-white hover:bg-[#1D4ED8]"
                aria-label={isPlaying ? "Pause recording" : "Play recording"}
              >
                {isPlaying ? (
                  <Pause className="h-4 w-4" fill="currentColor" />
                ) : (
                  <Play className="ml-0.5 h-4 w-4" fill="currentColor" />
                )}
              </button>
              <span className="w-9 shrink-0 font-mono text-[12px] font-semibold text-[#2563EB]">
                {formatClock(currentTime)}
              </span>
              <input
                type="range"
                min={0}
                max={totalDuration || 0}
                step={0.1}
                value={Math.min(currentTime, totalDuration || 0)}
                disabled={!totalDuration}
                onChange={(e) => seekRecording(Number(e.target.value))}
                className="order-last h-1.5 min-w-0 basis-full cursor-pointer appearance-none rounded-full border-0 outline-none accent-[#2563EB] sm:order-0 sm:flex-1 sm:basis-auto"
                style={{
                  background: `linear-gradient(to right, #2563EB ${progressPercent}%, #E5E7EB ${progressPercent}%)`,
                }}
                aria-label="Recording progress"
              />
              <span className="mr-auto w-9 shrink-0 font-mono text-[12px] text-muted-foreground sm:mr-0 sm:text-right">
                {formatClock(totalDuration)}
              </span>
              <button
                type="button"
                onClick={cycleSpeed}
                className="shrink-0 rounded-md border border-[#E5E7EB] px-2 py-0.5 text-[11px] font-semibold text-foreground hover:bg-[#F3F4F6]"
                aria-label="Playback speed"
              >
                {PLAYBACK_SPEEDS[speedIndex].toFixed(1)}x
              </button>
              <button
                type="button"
                onClick={() => void downloadRecording()}
                disabled={isDownloading}
                className="shrink-0 text-muted-foreground hover:text-foreground disabled:opacity-50"
                aria-label="Download recording"
              >
                <Download className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <p className="text-[12px] text-muted-foreground">
              No recording available for this call.
            </p>
          )}
        </div>

        {/* Transcript */}
        <div className="-mx-6 -mb-6 min-h-0 flex-1 overflow-y-auto bg-[#F9FAFB] px-6 py-4">
          {utterances.length > 0 ? (
            <div className="space-y-4">
              {utterances.map((utterance, index) => {
                const isAgent = isCareTeamUtterance(utterance);
                const speaker = isAgent
                  ? agentName || "Care Team"
                  : patientName || "Caller";
                const number = !isAgent && utterance.phoneNumber
                  ? formatUSPhoneNumber(utterance.phoneNumber)
                  : "";
                return (
                  <div
                    key={`${utterance.startTime ?? index}-${index}`}
                    className="flex gap-3"
                  >
                    <div
                      className={cn(
                        "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
                        isAgent ? "bg-[#EFF6FF]" : "bg-[#ECFDF5]",
                      )}
                    >
                      {isAgent ? (
                        <Headset className="h-4 w-4 text-[#2563EB]" />
                      ) : (
                        <Phone className="h-4 w-4 text-[#059669]" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="mb-1.5 flex flex-wrap items-center gap-2 text-[12px]">
                        <span className="font-semibold text-foreground">
                          {speaker}
                        </span>
                        {number ? (
                          <span className="font-mono text-[11px] text-muted-foreground">
                            {number}
                          </span>
                        ) : null}
                        {utterance.startTime != null ? (
                          <span className="rounded bg-[#EFF6FF] px-1.5 py-0.5 font-mono text-[10px] font-semibold text-[#2563EB]">
                            {formatClock(utterance.startTime)}
                          </span>
                        ) : null}
                      </div>
                      <div className="whitespace-pre-wrap wrap-break-word rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 text-[13px] leading-relaxed text-[#374151]">
                        {String(utterance.text ?? "").trim()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : plainTranscript ? (
            <div className="whitespace-pre-wrap wrap-break-word rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 text-[13px] leading-relaxed text-[#374151]">
              {plainTranscript}
            </div>
          ) : (
            <p className="text-[12px] text-muted-foreground">
              No transcript available for this call.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
